import React, { useState, useEffect, useCallback } from 'react';
import { statsAPI } from '../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import DeferralCalendar from './DeferralCalendar';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedBrandId, setSelectedBrandId] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;
      if (selectedJobId) params.jobId = selectedJobId;
      if (selectedBrandId) params.brandId = selectedBrandId;
      
      const response = await statsAPI.getStats(params);
      setStats(response.data.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedJobId, selectedBrandId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleDateChange = (e) => {
    setDateRange({
      ...dateRange,
      [e.target.name]: e.target.value
    });
  };

  const handleDateSelect = (date) => {
    // Set both start and end to selected date for single day view
    const dateStr = date.toISOString().split('T')[0];
    setDateRange({
      startDate: dateStr,
      endDate: dateStr
    });
  };

  const handleCalendarDateSelect = (date) => {
    handleDateSelect(date);
  };

  const clearDateFilter = () => {
    setDateRange({
      startDate: '',
      endDate: ''
    });
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  const { summary, byDate, byBrand, byJob, recentEmails } = stats || {};

  // Prepare date-wise chart data
  const dateChartData = Object.entries(byDate || {}).map(([date, data]) => ({
    date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    fullDate: date,
    sent: data.sent || 0,
    failed: data.failed || 0,
    delivered: data.delivered || 0,
    opened: data.opened || 0,
    clicked: data.clicked || 0,
    bounced: data.bounced || 0,
    dropped: data.dropped || 0,
    deferred: data.deferred || 0,
  })).sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));

  // Prepare brand-wise chart data
  const brandChartData = Object.values(byBrand || {}).map(brand => ({
    name: brand.brandId === 'Unknown' || !brand.brandId ? 'Unknown' : brand.brandId,
    sent: brand.sent || 0,
    failed: brand.failed || 0,
    delivered: brand.delivered || 0,
    deferred: brand.deferred || 0,
    total: brand.total || 0,
    successRate: parseFloat(brand.successRate || 0),
  })).sort((a, b) => b.total - a.total);

  // Prepare pie chart data for brand distribution
  const brandPieData = brandChartData.map(brand => ({
    name: brand.name,
    value: brand.total
  }));

  const COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#00f2fe', '#43e97b', '#fa709a', '#fee140', '#30cfd0', '#a8edea'];

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="page-title">Email Dashboard</h1>
        <div className="dashboard-controls">
          <div className="date-filter">
            <label>Start Date:</label>
            <input
              type="date"
              name="startDate"
              value={dateRange.startDate}
              onChange={handleDateChange}
              style={{ marginRight: '1rem' }}
            />
            <label>End Date:</label>
            <input
              type="date"
              name="endDate"
              value={dateRange.endDate}
              onChange={handleDateChange}
              style={{ marginRight: '1rem' }}
            />
            {(dateRange.startDate || dateRange.endDate) && (
              <button
                className="btn btn-secondary"
                onClick={clearDateFilter}
              >
                Clear Filter
              </button>
            )}
          </div>
          <div className="filter-group" style={{ marginTop: '1rem' }}>
            <label>Job ID:</label>
            <input
              type="text"
              value={selectedJobId}
              onChange={(e) => setSelectedJobId(e.target.value)}
              placeholder="Filter by Job ID"
              style={{ marginRight: '1rem', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
            <label>Brand ID:</label>
            <input
              type="text"
              value={selectedBrandId}
              onChange={(e) => setSelectedBrandId(e.target.value)}
              placeholder="Filter by Brand ID"
              style={{ marginRight: '1rem', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
            {(selectedJobId || selectedBrandId) && (
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setSelectedJobId('');
                  setSelectedBrandId('');
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{summary?.totalSent || 0}</div>
          <div className="stat-label">Total Sent</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{summary?.totalDelivered || 0}</div>
          <div className="stat-label">Delivered</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{summary?.totalOpened || 0}</div>
          <div className="stat-label">Opened</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{summary?.totalClicked || 0}</div>
          <div className="stat-label">Clicked</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{summary?.totalFailed || 0}</div>
          <div className="stat-label">Failed</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{summary?.totalBounced || 0}</div>
          <div className="stat-label">Bounced</div>
        </div>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' }}>
          <div className="stat-value">{summary?.totalDeferred || 0}</div>
          <div className="stat-label">Deferred</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{summary?.successRate || 0}%</div>
          <div className="stat-label">Success Rate</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{summary?.deliveryRate || 0}%</div>
          <div className="stat-label">Delivery Rate</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{summary?.openRate || 0}%</div>
          <div className="stat-label">Open Rate</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{summary?.clickRate || 0}%</div>
          <div className="stat-label">Click Rate</div>
        </div>
      </div>

      {/* Deferral Calendar */}
      <div className="card">
        <h2 className="card-title">📅 Deferral Calendar - Email Throttling by Day</h2>
        <p style={{ color: '#666', marginBottom: '1rem' }}>
          Click on any date to view statistics for that day. Colors indicate deferral intensity.
        </p>
        <DeferralCalendar 
          dateRange={dateRange} 
          onDateSelect={handleCalendarDateSelect}
        />
      </div>

      {/* Date-wise Reports */}
      <div className="card">
        <h2 className="card-title">📅 Daily Email Activity (Sent vs Failed)</h2>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={dateChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" angle={-45} textAnchor="end" height={80} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="sent" stroke="#667eea" strokeWidth={2} name="Sent" />
            <Line type="monotone" dataKey="failed" stroke="#dc3545" strokeWidth={2} name="Failed" />
            <Line type="monotone" dataKey="delivered" stroke="#28a745" strokeWidth={2} name="Delivered" />
            <Line type="monotone" dataKey="opened" stroke="#17a2b8" strokeWidth={2} name="Opened" />
            <Line type="monotone" dataKey="clicked" stroke="#ffc107" strokeWidth={2} name="Clicked" />
            <Line type="monotone" dataKey="deferred" stroke="#ff9800" strokeWidth={2} name="Deferred" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="card">
        <h2 className="card-title">📊 Daily Email Volume Breakdown</h2>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={dateChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" angle={-45} textAnchor="end" height={80} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="sent" stackId="a" fill="#667eea" name="Sent" />
            <Bar dataKey="delivered" stackId="a" fill="#28a745" name="Delivered" />
            <Bar dataKey="opened" stackId="a" fill="#17a2b8" name="Opened" />
            <Bar dataKey="clicked" stackId="a" fill="#ffc107" name="Clicked" />
            <Bar dataKey="failed" fill="#dc3545" name="Failed" />
            <Bar dataKey="bounced" fill="#fd7e14" name="Bounced" />
            <Bar dataKey="dropped" fill="#6c757d" name="Dropped" />
            <Bar dataKey="deferred" fill="#ff9800" name="Deferred" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Brand-wise Reports */}
      <div className="card">
        <h2 className="card-title">🏢 Email Statistics by Brand</h2>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '300px' }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>Brand Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={brandPieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {brandPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div style={{ flex: '1', minWidth: '300px' }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>Brand Performance</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={brandChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="sent" fill="#667eea" name="Sent" />
                <Bar dataKey="delivered" fill="#28a745" name="Delivered" />
                <Bar dataKey="failed" fill="#dc3545" name="Failed" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Job-wise Statistics */}
      {byJob && Object.keys(byJob).length > 0 && (
        <div className="card">
          <h2 className="card-title">📊 Job Statistics</h2>
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Job ID</th>
                  <th>Total</th>
                  <th>Sent</th>
                  <th>Delivered</th>
                  <th>Opened</th>
                  <th>Clicked</th>
                  <th>Failed</th>
                  <th>Bounced</th>
                  <th>Dropped</th>
                  <th>Deferred</th>
                  <th>Success Rate</th>
                  <th>Delivery Rate</th>
                  <th>Open Rate</th>
                  <th>Click Rate</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(byJob).map((job, index) => (
                  <tr key={index}>
                    <td><strong style={{ fontSize: '0.85em' }}>{job.jobId}</strong></td>
                    <td>{job.total}</td>
                    <td>{job.sent}</td>
                    <td>{job.delivered}</td>
                    <td>{job.opened}</td>
                    <td>{job.clicked}</td>
                    <td>{job.failed}</td>
                    <td>{job.bounced}</td>
                    <td>{job.dropped}</td>
                    <td>
                      <span className="badge" style={{ backgroundColor: '#ff9800', color: 'white' }}>
                        {job.deferred}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${parseFloat(job.successRate) >= 90 ? 'badge-success' : parseFloat(job.successRate) >= 70 ? 'badge-warning' : 'badge-danger'}`}>
                        {job.successRate}%
                      </span>
                    </td>
                    <td>
                      <span className="badge badge-info">
                        {job.deliveryRate}%
                      </span>
                    </td>
                    <td>
                      <span className="badge badge-info">
                        {job.openRate}%
                      </span>
                    </td>
                    <td>
                      <span className="badge badge-info">
                        {job.clickRate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Brand-wise Detailed Table */}
      {brandChartData.length > 0 && (
        <div className="card">
          <h2 className="card-title">📋 Detailed Brand Statistics</h2>
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Brand ID</th>
                  <th>Total</th>
                  <th>Sent</th>
                  <th>Delivered</th>
                  <th>Opened</th>
                  <th>Clicked</th>
                  <th>Failed</th>
                  <th>Bounced</th>
                  <th>Dropped</th>
                  <th>Deferred</th>
                  <th>Success Rate</th>
                  <th>Delivery Rate</th>
                  <th>Open Rate</th>
                  <th>Click Rate</th>
                </tr>
              </thead>
              <tbody>
                {brandChartData.map((brand, index) => {
                  const brandData = Object.values(byBrand || {}).find(b => 
                    (b.brandId || 'Unknown') === brand.name
                  );
                  return (
                    <tr key={index}>
                      <td><strong>{brand.name}</strong></td>
                      <td>{brand.total}</td>
                      <td>{brand.sent}</td>
                      <td>{brand.delivered}</td>
                      <td>{brandData?.opened || 0}</td>
                      <td>{brandData?.clicked || 0}</td>
                      <td>{brand.failed}</td>
                      <td>{brandData?.bounced || 0}</td>
                      <td>{brandData?.dropped || 0}</td>
                      <td>
                        <span className="badge" style={{ backgroundColor: '#ff9800', color: 'white' }}>
                          {brandData?.deferred || 0}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${brand.successRate >= 90 ? 'badge-success' : brand.successRate >= 70 ? 'badge-warning' : 'badge-danger'}`}>
                          {brand.successRate}%
                        </span>
                      </td>
                      <td>
                        <span className="badge badge-info">
                          {brandData?.deliveryRate || '0.00'}%
                        </span>
                      </td>
                      <td>
                        <span className="badge badge-info">
                          {brandData?.openRate || '0.00'}%
                        </span>
                      </td>
                      <td>
                        <span className="badge badge-info">
                          {brandData?.clickRate || '0.00'}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Emails */}
      {recentEmails && recentEmails.length > 0 && (
        <div className="card">
          <h2 className="card-title">📧 Recent Emails</h2>
          <table className="table">
            <thead>
              <tr>
                <th>To</th>
                <th>From</th>
                <th>Template ID</th>
                <th>Brand ID</th>
                <th>Status</th>
                <th>Sent At</th>
              </tr>
            </thead>
            <tbody>
              {recentEmails.map((email) => (
                <tr key={email.id}>
                  <td>{email.to}</td>
                  <td>{email.from}</td>
                  <td style={{ fontSize: '0.85em' }}>{email.templateId || 'N/A'}</td>
                  <td>{email.brandId || 'N/A'}</td>
                  <td>
                    <span className={`badge ${
                      email.status === 'sent' || email.status === 'delivered' ? 'badge-success' :
                      email.status === 'opened' || email.status === 'clicked' ? 'badge-info' :
                      'badge-danger'
                    }`}>
                      {email.status}
                    </span>
                  </td>
                  <td>{email.sentAt ? new Date(email.sentAt).toLocaleString() : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

